'use client';

import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { cn, formatUSD, gridHref, isModifiedClick } from '@/lib/utils';
import { HV_TAKEOVER_GRID_ID } from '@/lib/constants/addresses';
import { BoardroomIcon } from '@/components/shared';
import { CoinImage } from '@/components/shared/coin-image';
import {
  calculateSummary,
  type CoinSeatsData,
} from '@/components/seats/coin-seats-card';

function MiniGridHud({
  ownedSeats,
  totalSeats = 100,
  className,
}: {
  ownedSeats: number[];
  totalSeats?: number;
  className?: string;
}) {
  const ownedSet = new Set(ownedSeats);
  const cols = Math.ceil(Math.sqrt(totalSeats));
  return (
    // Cells are CSS-vars so the same hud can render at 6px on mobile
    // (where the previous 10px x 16-col hud took ~160px and pushed the
    // board name + value + view button into each other) and 10px on
    // desktop where there's room. `aspect-square` keeps tile shape on
    // both breakpoints.
    <div
      className={cn(
        'grid gap-px rounded-[3px] border border-amber-500/20 bg-amber-950/40 p-px shrink-0',
        className,
      )}
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, var(--mini-cell, 10px)))`,
      }}
    >
      {Array.from({ length: totalSeats }, (_, i) => (
        <div
          key={i}
          className={cn(
            'aspect-square rounded-[1px]',
            ownedSet.has(i) ? 'bg-amber-400/85' : 'bg-zinc-800/60',
          )}
        />
      ))}
    </div>
  );
}

export interface ProfileTilesListProps {
  groups: CoinSeatsData[];
  takeoverPriceUsd: number;
  onOpenGrid: (args: {
    poolId: string;
    isV2: boolean;
    memecoinAddress?: string;
  }) => void;
}

/**
 * Read-only list of boards where the profile holds tiles, with total seat value per board.
 */
export function ProfileTilesList({
  groups,
  takeoverPriceUsd,
  onOpenGrid,
}: ProfileTilesListProps) {
  if (groups.length === 0) {
    return (
      <div className="rounded-[4px] border border-dashed border-amber-500/20 bg-black/25 py-10 text-center px-3">
        <p className="text-zinc-400 text-sm">No tiles held yet</p>
        <p className="text-xs text-zinc-600 mt-1 font-mono">
          Grids you hold seats on appear here with total tile value.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {groups.map((g) => {
        const { totalSeatValue } = calculateSummary([g], takeoverPriceUsd);
        const valueLabel = formatUSD(totalSeatValue);
        const isBoardroom = g.poolId === HV_TAKEOVER_GRID_ID;
        const href = gridHref(g.poolId);
        const ownedIndices = g.seats.map((s) => s.seatIndex);
        const total = g.totalSeats ?? 100;

        return (
          <li key={g.poolId}>
            {/* Mobile stacks the row in two visual lines (info row +
                value/cta row) because the original 4-column flex was
                squeezing the board name, "Tile value" label and the
                value itself into the same horizontal slot — at <340px
                viewports the labels visibly overlapped the values. The
                `--mini-cell` CSS var also shrinks the MiniGridHud cells
                from 10→6px on mobile so the hud doesn't eat half the
                row. Desktop keeps the original single-row layout. */}
            <div
              className="rounded-[4px] border border-amber-500/15 bg-black/35 px-3 py-2.5 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
              style={{ ['--mini-cell' as unknown as string]: '6px' } as React.CSSProperties}
            >
              <div className="flex gap-3 items-center min-w-0 sm:[--mini-cell:10px]">
                <MiniGridHud ownedSeats={ownedIndices} totalSeats={total} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {isBoardroom ? (
                      <span className="flex h-8 w-8 items-center justify-center rounded-[4px] border border-amber-500/25 bg-black/40 shrink-0">
                        <BoardroomIcon size="md" />
                      </span>
                    ) : (
                      <div className="h-8 w-8 rounded-[4px] overflow-hidden border border-amber-500/20 bg-black/40 shrink-0">
                        <CoinImage
                          src={g.poolImageUrl}
                          alt={g.poolSymbol}
                          symbol={g.poolSymbol}
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-[12px] font-semibold text-zinc-100 truncate">
                        {isBoardroom ? 'Boardroom' : g.poolName || g.poolSymbol}
                      </p>
                      <p className="text-[10px] font-mono text-zinc-500">
                        {g.seats.length} seat{g.seats.length === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 sm:gap-3 sm:ml-auto shrink-0">
                <div className="text-left sm:text-right">
                  <p className="text-[9px] sm:text-[10px] font-mono uppercase tracking-[0.18em] sm:tracking-[0.2em] text-zinc-500 leading-tight">
                    Tile value
                  </p>
                  <p className="text-[13px] sm:text-[14px] font-mono tabular-nums text-amber-100/95 leading-tight">
                    {valueLabel}
                  </p>
                </div>
                <Link
                  href={href}
                  className="shrink-0 rounded-[3px] border border-amber-500/25 px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.15em] text-amber-200/90 hover:bg-amber-500/10 transition-colors inline-flex items-center gap-1"
                  onClick={(e) => {
                    if (isModifiedClick(e)) return;
                    e.preventDefault();
                    onOpenGrid({
                      poolId: g.poolId,
                      isV2: !!g.isV2Grid,
                      memecoinAddress: g.memecoinAddress,
                    });
                  }}
                >
                  View
                  <ChevronRight className="h-3 w-3 opacity-70" />
                </Link>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
